import type {IDocumentData, PcbRulesMap, PcbRuleSet} from "@buildwithflux/models";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";

/**
 * Base selectors for PCB layout rule sets - direct state reads
 */

/** Gets a PCB layout rule set by UID */
export const byUid: ParameterizedSelector<string, PcbRuleSet | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.pcbLayoutRuleSets?.[uid];
};

/** Gets only the global PCB layout rules (those with referenceOfDocumentUid) */
export const globalRules: DocumentSelector<IDocumentData["pcbLayoutRuleSets"] | undefined> = (
    state: DocumentStoreState,
) => {
    const rules = state.pcbLayoutRuleSets;
    if (!rules) return undefined;

    const result: typeof rules = {};
    for (const [uid, rule] of Object.entries(rules)) {
        if ("referenceOfDocumentUid" in rule) {
            result[uid] = rule;
        }
    }
    return result;
};

/** Checks if a PCB layout rule set is disabled */
export const disabledByUid: ParameterizedSelector<string, boolean> = (ruleUid: string) => {
    return (state: DocumentStoreState) => {
        const rule = state.pcbLayoutRuleSets?.[ruleUid];
        if (rule && "referenceOfDocumentUid" in rule) {
            return rule.disabled ?? false;
        }
        return false;
    };
};

/** Gets the object-specific rules for a PCB layout node */
export const nodeSpecificRules: ParameterizedSelector<string, PcbRulesMap> = (nodeUid: string) => {
    return (state: DocumentStoreState) => state.pcbLayoutNodes?.[nodeUid]?.pcbNodeRuleSet ?? {};
};
